import {
  Paper,
  TextField,
  Box,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  Alert,
} from '@mui/material'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import * as yup from 'yup'
import CircularProgress from '@mui/material/CircularProgress'
import {VisibilityOff, Visibility} from '@mui/icons-material'
import {isAxiosError} from 'axios'
import {useBooleanState} from '../../../components/hooks/useBooleanState'
import {useFormikTextFieldHelpers} from '../../../components/hooks/useFormikTextFieldHelpers'
import * as auth from '../AuthApi'

const LoginPage = () => {
  const navigate = useNavigate()
  const {
    enableState: showPassword,
    disableState: hidePassword,
    state: isPasswordShown,
  } = useBooleanState(false)
  const {hash} = useParams<{hash?: string}>()

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, {setStatus}) => {
      setStatus('')
      try {
        if (hash) {
          const response = await auth.validateHash(values.username, values.password, hash)
          navigate('/auth/mfa-setup', {
            state: response.data,
          })
        } else {
          const response = await auth.login(values.username, values.password)
          if (response.status === 202) {
            navigate('/auth/mfa', {
              state: response.data,
            })
          }
        }
      } catch (e) {
        let message = 'Unknown error has occurred, please try again later.'
        if (isAxiosError<{error?: string}>(e)) {
          const errorMessage = e.response?.data?.error
          if (errorMessage) {
            message = errorMessage
          }
        } else if (e instanceof Error) {
          message = e.message
        }
        setStatus(message)
      }
    },
  })
  const formikTextInputHelper = useFormikTextFieldHelpers(formik)

  return (
    <Paper
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        overflow: 'hidden',
        width: '80vw',
        maxWidth: '30rem',
      }}
    >
      <Box
        sx={(theme) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          textAlign: 'center',
          textTransform: 'uppercase',
          p: theme.spacing(2),
        })}
      >
        <Typography
          component='h1'
          variant='h6'
          sx={{
            letterSpacing: '0.1em',
            fontWeight: '700',
          }}
        >
          Sign In
        </Typography>
      </Box>
      <Box
        component='form'
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          padding: theme.spacing(5),
          [theme.breakpoints.down('md')]: {
            padding: theme.spacing(3),
          },
          [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(2),
          },
        })}
        autoComplete='off'
        onSubmit={formik.handleSubmit}
      >
        {formik.status && (
          <Alert severity='error' sx={(theme) => ({mb: theme.spacing(3)})}>
            {formik.status}
          </Alert>
        )}
        <TextField
          InputLabelProps={{
            shrink: true,
          }}
          id='field-email'
          autoFocus
          label='Email'
          placeholder='Enter Email'
          type='email'
          fullWidth
          {...formikTextInputHelper.getFieldProps('username')}
        />
        <Box>
          <TextField
            InputLabelProps={{
              shrink: true,
            }}
            id='field-password'
            label='Password'
            placeholder='Enter Password'
            fullWidth
            sx={(theme) => ({textDecoration: 'none'})}
            type={isPasswordShown ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    id='button-toggle-password-visibility'
                    aria-label='toggle password visibility'
                    onPointerDown={showPassword}
                    onPointerLeave={hidePassword}
                    onPointerUp={hidePassword}
                    edge='end'
                  >
                    {isPasswordShown ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...formikTextInputHelper.getFieldProps('password')}
          />
          <Typography
            id='link-forgot-password'
            fontWeight='bold'
            variant='subtitle2'
            component={Link}
            to='/auth/forgot-password'
            color='primary'
            sx={{textDecoration: 'none'}}
          >
            Forgot Password?
          </Typography>
        </Box>
        <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
          <Button
            sx={{positon: 'relative'}}
            disabled={formik.isSubmitting}
            type='submit'
            variant='contained'
            color='primary'
            id='button-submit'
          >
            Log In
            {formik.isSubmitting && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}

const initialValues = {
  username: '',
  password: '',
}

const validationSchema = yup.object().shape({
  username: yup.string().required('Please enter your username.'),
  password: yup.string().required('Please enter your password.'),
})

export default LoginPage
